import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import './Cadastro.css';

function formatCNPJ(value) {
  const d = (value || '').replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function formatTelefone(value) {
  const d = (value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function isValidCNPJ(cnpj) {
  const d = (cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(d)) return false;
  // Validação dos 2 dígitos verificadores (módulo 11)
  const calcDV = (base) => {
    let pos = base.length - 7;
    let soma = 0;
    for (let i = base.length; i >= 1; i--) {
      soma += parseInt(base[base.length - i], 10) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const dv1 = calcDV(d.slice(0, 12));
  if (dv1 !== parseInt(d[12], 10)) return false;
  const dv2 = calcDV(d.slice(0, 13));
  if (dv2 !== parseInt(d[13], 10)) return false;
  return true;
}

export default function Cadastro() {
  const [cnpj, setCnpj] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [senha2, setSenha2] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [verSenha2, setVerSenha2] = useState(false);
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const senhaForte = senha.length >= 8 && /[A-Za-z]/.test(senha) && /\d/.test(senha);
  const senhasIguais = senha && senha === senha2;
  const cnpjOk = isValidCNPJ(cnpj);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    if (!cnpjOk) return setErro('CNPJ inválido. Verifique os 14 dígitos.');
    if (!razaoSocial.trim()) return setErro('Informe a razão social.');
    if (!nome.trim()) return setErro('Informe o nome do responsável.');
    if (!email.trim()) return setErro('Informe o e-mail.');
    if (!senhaForte) return setErro('A senha deve ter pelo menos 8 caracteres, com letras e números.');
    if (!senhasIguais) return setErro('As senhas não conferem.');
    if (!aceiteTermos) return setErro('Você precisa aceitar os termos de uso para continuar.');

    setLoading(true);
    try {
      await register({
        nome: nome.trim(),
        email: email.trim(),
        senha,
        cnpj: cnpj.replace(/\D/g, ''),
        razaoSocial: razaoSocial.trim(),
        nomeFantasia: nomeFantasia.trim() || undefined,
        telefone: telefone.replace(/\D/g, '') || undefined,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setErro(
        err.response?.data?.error ||
        err.response?.data?.mensagem ||
        err.response?.data?.message ||
        'Erro ao criar conta. Verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cadastro-page">
      <form className="cadastro-card" onSubmit={handleSubmit} noValidate>
        <div className="cadastro-logo">
          <h1>LicitaPRO</h1>
        </div>
        <p className="cadastro-subtitle">Criar conta gratuita</p>

        {erro && <div className="cadastro-error">{erro}</div>}

        <fieldset className="cadastro-fieldset">
          <legend>Dados da empresa</legend>

          <div className="cadastro-field">
            <label htmlFor="cnpj">CNPJ <span className="req">*</span></label>
            <input
              id="cnpj"
              type="text"
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              value={cnpj}
              onChange={(e) => setCnpj(formatCNPJ(e.target.value))}
              autoFocus
              required
            />
            {cnpj && !cnpjOk && (
              <span className="cadastro-hint cadastro-hint-erro">CNPJ inválido</span>
            )}
          </div>

          <div className="cadastro-field">
            <label htmlFor="razaoSocial">Razão social <span className="req">*</span></label>
            <input
              id="razaoSocial"
              type="text"
              placeholder="Empresa LTDA"
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
              required
            />
          </div>

          <div className="cadastro-field">
            <label htmlFor="nomeFantasia">Nome fantasia</label>
            <input
              id="nomeFantasia"
              type="text"
              placeholder="Como sua empresa é conhecida"
              value={nomeFantasia}
              onChange={(e) => setNomeFantasia(e.target.value)}
            />
          </div>
        </fieldset>

        <fieldset className="cadastro-fieldset">
          <legend>Dados do responsável</legend>

          <div className="cadastro-field">
            <label htmlFor="nome">Nome completo <span className="req">*</span></label>
            <input
              id="nome"
              type="text"
              placeholder="João da Silva"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </div>

          <div className="cadastro-row">
            <div className="cadastro-field">
              <label htmlFor="email">E-mail <span className="req">*</span></label>
              <input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="cadastro-field">
              <label htmlFor="telefone">Telefone</label>
              <input
                id="telefone"
                type="tel"
                inputMode="numeric"
                placeholder="(11) 99999-9999"
                value={telefone}
                onChange={(e) => setTelefone(formatTelefone(e.target.value))}
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="cadastro-field">
            <label htmlFor="senha">Senha <span className="req">*</span></label>
            <div className="cadastro-input-wrap">
              <input
                id="senha"
                type={verSenha ? 'text' : 'password'}
                placeholder="Mínimo 8 caracteres"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="cadastro-toggle-senha"
                onClick={() => setVerSenha(!verSenha)}
                aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {verSenha ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {senha && (
              <span className={`cadastro-hint ${senhaForte ? 'cadastro-hint-ok' : 'cadastro-hint-erro'}`}>
                {senhaForte ? <><FiCheck /> Senha forte</> : 'Use 8+ caracteres com letras e números'}
              </span>
            )}
          </div>

          <div className="cadastro-field">
            <label htmlFor="senha2">Confirmar senha <span className="req">*</span></label>
            <div className="cadastro-input-wrap">
              <input
                id="senha2"
                type={verSenha2 ? 'text' : 'password'}
                placeholder="Repita a senha"
                value={senha2}
                onChange={(e) => setSenha2(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="cadastro-toggle-senha"
                onClick={() => setVerSenha2(!verSenha2)}
                aria-label={verSenha2 ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {verSenha2 ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {senha2 && !senhasIguais && (
              <span className="cadastro-hint cadastro-hint-erro">As senhas não conferem</span>
            )}
          </div>
        </fieldset>

        <label className="cadastro-checkbox">
          <input
            type="checkbox"
            checked={aceiteTermos}
            onChange={(e) => setAceiteTermos(e.target.checked)}
          />
          <span>
            Li e aceito os <a href="/termos" target="_blank" rel="noopener noreferrer">termos de uso</a>{' '}
            e a <a href="/privacidade" target="_blank" rel="noopener noreferrer">política de privacidade</a>.
          </span>
        </label>

        <button className="cadastro-btn" type="submit" disabled={loading}>
          {loading ? 'Criando conta...' : 'Criar conta'}
        </button>

        <p className="cadastro-rodape">
          Já tem conta? <Link to="/login">Entrar</Link>
        </p>
      </form>
    </div>
  );
}
